import React, { useEffect } from 'react';
import { Dialog } from 'components';
import { DateTime } from 'luxon';
import { useForm } from 'react-hook-form';
import { UploadInvoiceFormFields } from './types';
import { UploadInvoiceForm } from './UploadInvoiceForm';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadInvoice, generateInvoice } from 'api/invoices';
import { useSearchParams } from 'react-router-dom';
import { useNotifications } from 'hooks';

type UploadInvoiceDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const UploadInvoiceDialog = ({ open, onClose }: UploadInvoiceDialogProps) => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaignId');

  const { setNotification } = useNotifications();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<UploadInvoiceFormFields>({
    defaultValues: {
      businessId: null,
      amount: null,
      date: DateTime.now(),
      campaignId,
      invoiceFile: null,
      autoGenerate: true,
    },
    criteriaMode: 'all',
  });

  const autoGenerate = watch('autoGenerate');

  // if we close modal and change campaignId in list filter we should update the form with new campaignId
  useEffect(() => {
    if (campaignId) {
      setValue('campaignId', campaignId);
    }
  }, [campaignId, setValue]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const uploadInvoiceMutation = useMutation({
    mutationFn: uploadInvoice,
    mutationKey: ['uploadInvoice'],
    onSuccess: async () => {
      // invalidate all invoices list queries
      await queryClient.invalidateQueries({ queryKey: ['allInvoices'] });
      setNotification('Invoice uploaded successfully', 'success');
      handleClose();
    },
    onError: () => {
      setNotification('Failed to upload invoice', 'error');
    },
  });

  const generateInvoiceMutation = useMutation({
    mutationFn: generateInvoice,
    mutationKey: ['generateInvoice'],
    onSuccess: async () => {
      // invalidate all invoices list queries
      await queryClient.invalidateQueries({ queryKey: ['allInvoices'] });
      setNotification('Invoice generated successfully', 'success');
      handleClose();
    },
    onError: () => {
      setNotification('Failed to generate invoice', 'error');
    },
  });

  const onSubmit = (data: UploadInvoiceFormFields) => {
    if (data.autoGenerate) {
      // Generate invoice
      generateInvoiceMutation.mutate({
        businessInvoiceId: data.businessId ?? '',
        amount: data.amount ?? '',
        invoiceDate: data.date.toISODate() ?? '',
        campaignId: data.campaignId ?? '',
      });
    } else {
      // Upload invoice
      const formData = new FormData();
      formData.append('businessInvoiceId', data.businessId ?? '');
      formData.append('amount', data.amount ?? '');
      formData.append('invoiceDate', data.date.toISODate() ?? '');
      formData.append('campaignId', data.campaignId ?? '');
      formData.append('file', data.invoiceFile as File);

      uploadInvoiceMutation.mutate(formData);
    }
  };

  const isLoading = uploadInvoiceMutation.isPending || generateInvoiceMutation.isPending;

  return (
    <Dialog
      size="xs"
      content={<UploadInvoiceForm form={{ register, errors, control, watch, setValue }} />}
      open={open}
      onClose={handleClose}
      title={autoGenerate ? 'Generate invoice' : 'Upload custom invoice'}
      primaryActionTitle={autoGenerate ? 'Generate' : 'Upload'}
      onPrimaryActionClick={handleSubmit(onSubmit)}
      onSecondaryActionClick={handleClose}
      isPrimaryActionLoading={isLoading}
      secondaryActionTitle="Cancel"
    />
  );
};
